import { CheckCircle2, ListChecks, CheckSquare, TrendingUp, Eye, Star } from "lucide-react";
import { FreelancerListItem } from "@/types/freelancer";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

interface FreelancerSignalsRowProps {
  freelancer: FreelancerListItem;
  density?: "compact" | "comfortable";
  className?: string;
}

interface SignalItem {
  key: string;
  icon: typeof Star;
  value: number | string;
  label: string;
  iconClass: string;
  show: boolean;
}

/**
 * Linha enxuta com os principais contadores de sinais do freelancer.
 * Itens sem valor são omitidos (não polui o card de freelancers sem histórico).
 */
export function FreelancerSignalsRow({
  freelancer,
  density = "comfortable",
  className,
}: FreelancerSignalsRowProps) {
  const items: SignalItem[] = [
    {
      key: "aprovacoes",
      icon: CheckCircle2,
      value: freelancer.total_aprovacoes ?? 0,
      label: "aprovações em vagas",
      iconClass: "text-emerald-600",
      show: (freelancer.total_aprovacoes ?? 0) > 0,
    },
    {
      key: "shortlists",
      icon: ListChecks,
      value: freelancer.total_shortlists ?? 0,
      label: "vezes em shortlist",
      iconClass: "text-blue-600",
      show: (freelancer.total_shortlists ?? 0) > 0,
    },
    {
      key: "projetos",
      icon: CheckSquare,
      value: freelancer.total_projetos_concluidos ?? 0,
      label: "projetos concluídos",
      iconClass: "text-indigo-600",
      show: (freelancer.total_projetos_concluidos ?? 0) > 0,
    },
    {
      key: "conversao",
      icon: TrendingUp,
      value:
        typeof freelancer.taxa_conversao_shortlist === "number"
          ? `${Math.round(freelancer.taxa_conversao_shortlist)}%`
          : "—",
      label: "taxa de conversão (passou pela shortlist / selecionados)",
      iconClass: "text-purple-600",
      show:
        typeof freelancer.taxa_conversao_shortlist === "number" &&
        (freelancer.total_vagas_selecionado ?? 0) > 0,
    },
  ];

  const totalViews = freelancer.total_views;
  if (typeof totalViews === "number" && totalViews > 0) {
    items.push({
      key: "views",
      icon: Eye,
      value: totalViews,
      label: "visualizações do perfil (não entram no score)",
      iconClass: "text-slate-500",
      show: true,
    });
  }

  const visible = items.filter((i) => i.show);
  if (visible.length === 0) return null;

  const spacing = density === "compact" ? "gap-2" : "gap-3";
  const textSize = density === "compact" ? "text-[11px]" : "text-xs";

  return (
    <TooltipProvider>
      <div
        className={cn(
          "flex flex-wrap items-center text-muted-foreground",
          spacing,
          textSize,
          className,
        )}
      >
        {visible.map(({ key, icon: Icon, value, label, iconClass }) => (
          <Tooltip key={key}>
            <TooltipTrigger asChild>
              <span className="inline-flex items-center gap-1">
                <Icon className={cn("h-3.5 w-3.5", iconClass)} />
                <span className="font-medium text-foreground">{value}</span>
              </span>
            </TooltipTrigger>
            <TooltipContent>{label}</TooltipContent>
          </Tooltip>
        ))}
      </div>
    </TooltipProvider>
  );
}
